import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Download, Info } from '../../icons';
import { listEmployees, type EmployeeRowDto } from '../../api/hr-employee';
// 옮겨 단 현황 다섯(2026-10-18 · web 레인 W10-26 · 사용자 결정) — 이미 부르던 응답 그대로 · 서버 0
import { getHeadcountTrend, type HeadcountTrendDto } from '../../api/hr-employee';
import { listRecords } from '../../api/hr-records';
import { CareerLine, MonthlyHeadcountCard, trendMonthsFor } from './HrStatsMoved';
import './hr-stats-moved.css';
import { listLeaveQuotas, type QuotaRowDto } from '../../api/hr-admin';
import { getOrgLeaders } from '../../api/hr-org-leader';
import { listOffboardings } from '../../api/hr-offboarding';
import { listAssignments } from '../../api/hr-assignment';
import {
  buildHrStats, hrStatsCsv, asOfForYear, dateOnlyOf,
  type HrStats, type HrStatsGroupBy, type ResignRecord,
} from '../../lib/hr-stats';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './HrStatsPage.css';

/**
 * 인사 통계 — 담당자 화면 (시안 A18).
 *
 * ★★ **새로 만드는 값이 없다.** 명부 · 연차 현황 · 대표·부서장(팀→본부 지도) · 퇴사 기록,
 *    담당자가 이미 부르는 네 응답을 세로로 세운 것이 전부다. 집계는 `lib/hr-stats.ts` 한 곳.
 * ★ **못 세는 것은 지어내지 않는다.** 시안의 «평균 연령» 은 생년이 저장되지 않아(생일은 연도
 *   없는 `MM-DD`) 만들 수 없다 — 화면 맨 아래가 그 사실을 말한다.
 * ★ 곁가지 응답(본부 지도 · 퇴사 기록)은 **따로 삼킨다** — 옛 서버·권한 어느 쪽이든 그 칸만
 *   빠지고 화면은 선다(인사 홈·팀 근태가 쓰는 것과 같은 태도).
 */

/** 막대 높이(px) — 칸 높이 108 을 최댓값에 맞춰 나눈다. 0 이 아닌 값은 최소 3px 로 보인다. */
function barHeight(value: number, max: number): number {
  if (value <= 0) return 0;
  if (max <= 0) return 0;
  return Math.max(3, Math.round((value / max) * 108));
}

const GROUPS: Array<{ key: HrStatsGroupBy; label: string }> = [
  { key: 'division', label: '본부별' },
  { key: 'team', label: '팀별' },
];

export function HrStatsPage() {
  const today = dateOnlyOf(new Date());
  const thisYear = Number(today.slice(0, 4));

  const [year, setYear] = useState(thisYear);
  const [groupBy, setGroupBy] = useState<HrStatsGroupBy>('team');
  const [employees, setEmployees] = useState<readonly EmployeeRowDto[]>([]);
  const [quotas, setQuotas] = useState<readonly QuotaRowDto[]>([]);
  const [divisionByTeamName, setDivisionByTeamName] = useState<ReadonlyMap<string, string>>(new Map());
  const [resigns, setResigns] = useState<readonly ResignRecord[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  // W10-26 — 입사 예정 · 퇴사 예정(직원 목록 요약) · 월별 재직(추이) · 입사 전 경력(인사기록 요약). 못 읽으면 그 칸만 접는다.
  const [empSummary, setEmpSummary] = useState<{ upcomingHires?: number; upcomingResigns?: number }>({});
  const [trend, setTrend] = useState<HeadcountTrendDto | null | undefined>(undefined);
  const [records, setRecords] = useState<{ total: number; withCareer: number; avgCareerYears: number } | null | undefined>(undefined);

  const asOf = asOfForYear(year, today);

  useEffect(() => {
    let alive = true;
    setState('loading');
    (async () => {
      try {
        const [emp, quota] = await Promise.all([listEmployees(), listLeaveQuotas(year)]);
        if (!alive) return;
        setEmployees(emp.rows);
        setEmpSummary({ upcomingHires: emp.upcomingHires, upcomingResigns: emp.upcomingResigns });
        setQuotas(quota.rows);
        setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [year]);

  // 월별 재직(W10-26) — 1월부터 이번 달까지 + 앞달 하나(전달 대비). 서버는 «오늘부터 최근 1~12달»이다.
  useEffect(() => {
    let alive = true;
    getHeadcountTrend(trendMonthsFor(Number(today.slice(5, 7))))
      .then((t) => { if (alive) setTrend(t); })
      .catch(() => { if (alive) setTrend(null); });
    return () => { alive = false; };
  }, [today]);
  // 입사 전 경력(W10-26) — 55 경력 화면이 부르는 요약 그대로(거르기 없이 전체).
  useEffect(() => {
    let alive = true;
    listRecords()
      .then((r) => { if (alive) setRecords({ total: r.total, withCareer: r.summary.withCareer, avgCareerYears: r.summary.avgCareerYears }); })
      .catch(() => { if (alive) setRecords(null); });
    return () => { alive = false; };
  }, []);

  // 본부 지도 — 없으면 «본부별» 이 통째로 «본부 미지정» 이 되므로 그 단추를 잠근다.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const org = await getOrgLeaders();
        if (!alive) return;
        const nameOfDivision = new Map(org.divisions.map((d) => [d.id, d.name]));
        const map = new Map<string, string>();
        for (const t of org.teams) {
          const division = t.divisionId ? nameOfDivision.get(t.divisionId) : undefined;
          if (division) map.set(t.name, division);
        }
        setDivisionByTeamName(map);
      } catch { /* 본부로 묶는 길만 닫힌다 */ }
    })();
    return () => { alive = false; };
  }, []);

  /**
   * 퇴사 기록 — **두 곳에서 온다.** 퇴사 처리(오프보딩)는 «언제 나가기로 했나» 이고,
   * 퇴직 발령(시행)은 «실제로 그날 상태가 바뀌었다» 이다. 둘 다 없는 회사도 있어서
   * 하나라도 못 읽으면 그 쪽만 빠진다. 같은 사람이 둘 다 가지면 집계가 접는다.
   */
  useEffect(() => {
    let alive = true;
    (async () => {
      const rows: ResignRecord[] = [];
      try {
        const res = await listOffboardings();
        for (const o of res.offboardings) {
          if (o.status !== 'CANCELED') rows.push({ userId: o.userId, date: o.resignDate });
        }
      } catch { /* 퇴사 처리 기록만 빠진다 */ }
      try {
        const res = await listAssignments();
        for (const a of res.assignments) {
          if (a.kind === 'RESIGN' && a.status === 'APPLIED') rows.push({ userId: a.userId, date: a.effectiveDate });
        }
      } catch { /* 퇴직 발령 기록만 빠진다 */ }
      if (alive) setResigns(rows);
    })();
    return () => { alive = false; };
  }, []);

  const canDivision = divisionByTeamName.size > 0;
  const effectiveGroupBy: HrStatsGroupBy = groupBy === 'division' && !canDivision ? 'team' : groupBy;

  const stats: HrStats | null = useMemo(() => {
    if (state !== 'ok') return null;
    return buildHrStats({
      employees, quotas, divisionByTeamName, resigns, year, asOf, groupBy: effectiveGroupBy,
    });
  }, [state, employees, quotas, divisionByTeamName, resigns, year, asOf, effectiveGroupBy]);

  const groupLabel = effectiveGroupBy === 'team' ? '팀' : '본부';
  const years = Array.from({ length: 5 }, (_, i) => thisYear - i);

  const download = () => {
    if (!stats) return;
    const blob = new Blob([hrStatsCsv(stats, groupLabel)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `인사통계_${year}_${groupLabel}별.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxHeadcount = Math.max(1, ...(stats?.groups.map((g) => g.headcount) ?? [0]));
  const maxTenure = Math.max(1, ...(stats?.tenure.buckets.map((b) => b.count) ?? [0]));
  const maxMonth = Math.max(1, ...(stats?.monthly.flatMap((m) => [m.hired, m.left]) ?? [0]));

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/stats')}
        scope={scopeFor('/erp/hr/stats')}
        title="인사 통계"
        description="명부 · 근태 · 연차가 이미 갖고 있는 값을 세로로 세워 봅니다 — 여기서 새로 만드는 값은 없습니다"
      />

      <div className="hrstat__bar">
        <select
          className="hrstat__sel"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          aria-label="연도"
        >
          {years.map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>

        <div className="hrstat__seg" role="group" aria-label="묶는 단위">
          {GROUPS.map((g) => (
            <button
              key={g.key}
              type="button"
              className={`hrstat__segbtn${effectiveGroupBy === g.key ? ' is-on' : ''}`}
              onClick={() => setGroupBy(g.key)}
              disabled={g.key === 'division' && !canDivision}
              title={g.key === 'division' && !canDivision ? '부서(본부)가 아직 없습니다' : undefined}
              aria-pressed={effectiveGroupBy === g.key}
            >
              {g.label}
            </button>
          ))}
        </div>

        <span className="hrstat__asof">기준일 · {asOf}</span>

        <button type="button" className="hrstat__dl" onClick={download} disabled={!stats}>
          <Download size={15} aria-hidden />
          엑셀(CSV)
        </button>
      </div>

      {state === 'loading' && <div className="hrpage__note">불러오는 중…</div>}
      {state === 'folded' && (
        <div className="hrpage__note">
          통계를 불러오지 못했습니다 — 직원 명부와 연차 현황을 읽을 수 있어야 이 화면이 섭니다.
        </div>
      )}

      {stats && (
        <>
          <div className="hrpage__kpis hrstat__kpis">
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">재직</span>
              <span className="hrpage__kpiv">{stats.headcount.active}<small>명</small></span>
              <span className="hrpage__kpid">
                정규 {stats.headcount.regular} · 계약 {stats.headcount.contract}
                {stats.headcount.parttime > 0 ? ` · 단시간 ${stats.headcount.parttime}` : ''}
                {stats.headcount.typeUnset > 0 ? ` · 미지정 ${stats.headcount.typeUnset}` : ''}
                {stats.headcount.onLeave > 0 ? ` · 휴직 ${stats.headcount.onLeave}` : ''}
              </span>
            </div>

            <div className="hrpage__kpi">
              <span className="hrpage__kpil">평균 근속</span>
              <span className="hrpage__kpiv">
                {stats.tenure.avgYears ?? '—'}{stats.tenure.avgYears !== null && <small>년</small>}
              </span>
              <span className="hrpage__kpid">
                {stats.tenure.known > 0 ? `입사일 있는 ${stats.tenure.known}명 기준` : '입사일이 있는 사람이 없습니다'}
              </span>
            </div>

            <div className="hrpage__kpi">
              <span className="hrpage__kpil">{year}년 입사</span>
              <span className="hrpage__kpiv">{stats.hires.count}<small>명</small></span>
              <span className="hrpage__kpid">
                {year === thisYear ? `이번 달 ${stats.hires.thisMonth}명` : '지금 명부에 남아 있는 사람 기준'}
              </span>
              {/* W10-26 — 25 카드에서 옮긴 «입사 예정»(올해를 볼 때만 · 지금 값) */}
              {year === thisYear && empSummary.upcomingHires !== undefined && (
                <span className="hrpage__kpid">입사 예정 {empSummary.upcomingHires}명 · 출처 직원 관리</span>
              )}
            </div>

            <div className="hrpage__kpi">
              <span className="hrpage__kpil">{year}년 퇴사</span>
              <span className="hrpage__kpiv">
                {stats.leavers.hasRecords ? <>{stats.leavers.count}<small>명</small></> : '—'}
              </span>
              <span className="hrpage__kpid">
                {stats.leavers.turnoverRate !== null
                  ? `이직률 ${stats.leavers.turnoverRate}% · 재직 + 퇴사 대비`
                  : `퇴사 기록이 없습니다 · 상태 «퇴사» ${stats.leavers.statusResigned}명`}
              </span>
              {/* W10-26 — 25 카드에서 옮긴 «퇴사 예정»(올해를 볼 때만 · 지금 값) */}
              {year === thisYear && empSummary.upcomingResigns !== undefined && (
                <span className="hrpage__kpid">퇴사 예정 {empSummary.upcomingResigns}명 · 출처 직원 관리</span>
              )}
            </div>

            <div className="hrpage__kpi is-acc">
              <span className="hrpage__kpil">연차 소진율</span>
              <span className="hrpage__kpiv">{stats.quota.rate}<small>%</small></span>
              <span className="hrpage__kpid">
                부여 {stats.quota.total}일 · 사용 {stats.quota.used}일 · 잔여 {stats.quota.remain}일
              </span>
            </div>

            <div className={`hrpage__kpi${stats.gaps.missingHireDate > 0 ? ' is-warn' : ''}`}>
              <span className="hrpage__kpil">입사일 미설정</span>
              <span className="hrpage__kpiv">{stats.gaps.missingHireDate}<small>명</small></span>
              <span className="hrpage__kpid">
                {stats.gaps.missingHireDate > 0 ? '이 사람들의 근속·연차는 셈에 없다' : '전원 입력됨'}
              </span>
            </div>
          </div>

          <div className="hrpage__row">
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                {groupLabel}별 인원
                <span className="hrpage__panels">기준일 현재 재직 {stats.headcount.active}명</span>
              </h2>
              {stats.groups.length === 0 ? (
                <p className="hrpage__empty">아직 나눌 {groupLabel}이 없습니다.</p>
              ) : (
                <div className="hrstat__cols">
                  {stats.groups.slice(0, 10).map((g) => (
                    <div className="hrstat__col" key={g.label}>
                      <span className="hrstat__colv">{g.headcount}</span>
                      <div className="hrstat__pair">
                        <i
                          className="hrstat__barv"
                          style={{ height: `${barHeight(g.headcount, maxHeadcount)}px` }}
                          aria-hidden
                        />
                      </div>
                      <span className="hrstat__coll" title={g.label}>{g.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                직위 구성
                <span className="hrpage__panels">직급 높은 순 · 레벨 없는 직위는 뒤로</span>
              </h2>
              {stats.positions.length === 0 ? (
                <p className="hrpage__empty">재직자가 없습니다.</p>
              ) : (
                <div className="hrstat__list">
                  {stats.positions.slice(0, 8).map((p) => (
                    <div className="hrstat__row" key={p.label}>
                      <span className="hrstat__rowl" title={p.label}>{p.label}</span>
                      <span className="hrstat__rowbar">
                        <i style={{ width: `${Math.min(100, p.share)}%` }} />
                      </span>
                      <span className="hrstat__rowv">{p.count}명 · {p.share}%</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                근속 분포
                <span className="hrpage__panels">기준일 {asOf}</span>
              </h2>
              <div className="hrstat__cols">
                {stats.tenure.buckets.map((b) => (
                  <div className="hrstat__col" key={b.label}>
                    <span className="hrstat__colv">{b.count}</span>
                    <div className="hrstat__pair">
                      <i
                        className="hrstat__barv"
                        style={{ height: `${barHeight(b.count, maxTenure)}px` }}
                        aria-hidden
                      />
                    </div>
                    <span className="hrstat__coll" title={b.label}>{b.label}</span>
                  </div>
                ))}
              </div>
              {/* W10-26 — 55 카드에서 옮긴 입사 전 경력(근속 칸 옆 · 뜻을 한 줄로 가른다) */}
              <CareerLine records={records} />
            </section>
          </div>

          {/* W10-26 — 25 카드에서 옮긴 월별 재직 인원(1월부터 · 전달 대비 증감 · 0 기준) */}
          <MonthlyHeadcountCard trend={trend} year={year} thisYear={thisYear} />

          <div className="hrpage__row hrstat__rows">
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                월별 입 · 퇴사 ({year})
                <span className="hrpage__panels">
                  입사 {stats.hires.count}명 · 퇴사 {stats.leavers.hasRecords ? `${stats.leavers.count}명` : '기록 없음'}
                </span>
              </h2>
              <div className="hrstat__cols">
                {stats.monthly.map((m) => (
                  <div className="hrstat__col" key={m.month}>
                    <span className="hrstat__colv">{m.hired + m.left > 0 ? m.hired + m.left : ''}</span>
                    <div className="hrstat__pair">
                      <i
                        className="hrstat__barv"
                        style={{ height: `${barHeight(m.hired, maxMonth)}px` }}
                        aria-label={`${m.month}월 입사 ${m.hired}명`}
                      />
                      <i
                        className="hrstat__barv hrstat__barv--alt"
                        style={{ height: `${barHeight(m.left, maxMonth)}px` }}
                        aria-label={`${m.month}월 퇴사 ${m.left}명`}
                      />
                    </div>
                    <span className="hrstat__coll">{m.month}</span>
                  </div>
                ))}
              </div>
              <div className="hrstat__legend">
                <span><i className="is-main" />입사</span>
                <span><i className="is-alt" />퇴사</span>
              </div>
            </section>

            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                {groupLabel}별 연차 소진율
                <span className="hrpage__panels">덜 쓴 쪽이 촉진 대상 · 미사용은 연말에 소멸합니다</span>
              </h2>
              {stats.groups.length === 0 ? (
                <p className="hrpage__empty">연차 현황이 비어 있습니다.</p>
              ) : (
                <table className="hrpage__tbl">
                  <thead>
                    <tr>
                      <th>{groupLabel}</th>
                      <th className="hrpage__num">인원</th>
                      <th className="hrpage__num">부여</th>
                      <th className="hrpage__num">사용</th>
                      <th className="hrpage__bar">소진율</th>
                      <th className="hrpage__num">잔여</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.groups.map((g) => (
                      <tr key={g.label}>
                        <td>{g.label}</td>
                        <td className="hrpage__num">{g.headcount}</td>
                        <td className="hrpage__num">{g.total}</td>
                        <td className="hrpage__num">{g.used}</td>
                        <td className="hrpage__bar">
                          <span><i style={{ width: `${Math.min(100, g.rate)}%` }} /></span>
                        </td>
                        <td className="hrpage__num">{g.remain}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              <BarChart3 size={16} aria-hidden />
              지금 셀 수 없는 것
              <span className="hrpage__panels">그럴듯한 숫자를 지어내는 대신 적어 둡니다</span>
            </h2>
            <ul className="hrstat__notes">
              <li>
                <Info size={14} aria-hidden />
                <span>
                  <b>평균 연령</b> — 생년을 저장하지 않습니다(생일은 연도 없는 «월-일»). 나이를 만들 길이
                  없어 시안의 이 지표만 비워 둡니다.
                </span>
              </li>
              <li>
                <Info size={14} aria-hidden />
                <span>
                  <b>지난 해의 인원 구성</b> — 명부에는 «그때의 소속» 이 없어, 부서·직위·근속은 언제나
                  <b> 기준일 현재</b>입니다. 연도는 입 · 퇴사와 연차에만 걸립니다.
                </span>
              </li>
              <li>
                <Info size={14} aria-hidden />
                <span>
                  <b>퇴사</b> — <Link to="/erp/hr/offboarding">퇴사 처리</Link>와{' '}
                  <Link to="/erp/hr/assignments">퇴직 발령</Link>에 남은 날짜로 셉니다. 그 둘을 거치지 않고
                  상태만 «퇴사» 로 바뀐 사람은 월별 그림에 오지 않습니다
                  {stats.leavers.statusResigned > 0 ? ` (지금 ${stats.leavers.statusResigned}명).` : '.'}
                </span>
              </li>
              {stats.quota.missing > 0 && (
                <li>
                  <Info size={14} aria-hidden />
                  <span>
                    <b>연차 미산정 {stats.quota.missing}명</b> — 부여가 0으로 들어가 소진율이 실제보다 높게
                    보입니다. <Link to="/erp/attendance/leave">연차 관리</Link>에서 산정하면 이 화면이 함께 맞습니다.
                  </span>
                </li>
              )}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
